import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { nepalToday } from "@yatayat/core/date";

import { BsDateHint } from "~/components/BsDateHint";
import { resolveRange } from "~/components/charts/range";
import { isIsoDate } from "~/components/charts/time";
import { OverviewCorridors, type CorridorColor } from "~/components/console/OverviewCorridors";
import { OverviewDepartures } from "~/components/console/OverviewDepartures";
import { OverviewHeadlines } from "~/components/console/OverviewHeadlines";
import { useI18n } from "~/lib/i18n";
import { formatIsoDate } from "~/lib/labels";
import { placeCodePattern, searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type OverviewSearch = {
  /** The day of the headlines and the departures board; today when unset. */
  date?: string;
  /** The corridor map's period; the last 30 days when unset. */
  from?: string;
  to?: string;
  color?: CorridorColor;
  corridor?: string;
  terminal?: string;
};

// Everything on screen lives in the URL, so a reload or a shared link shows the same view.
export const Route = createFileRoute("/console/")({
  validateSearch: (search: Record<string, unknown>): OverviewSearch => ({
    date: searchString(search.date),
    from: searchString(search.from),
    to: searchString(search.to),
    color: search.color === "demand" ? "demand" : undefined,
    corridor: searchString(search.corridor),
    terminal: searchString(search.terminal),
  }),
  component: ConsoleOverview,
});

function ConsoleOverview() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const today = nepalToday();
  const date = isIsoDate(search.date) && search.date <= today ? search.date : today;
  const range = resolveRange(search, today);
  const terminal = search.terminal && placeCodePattern.test(search.terminal) ? search.terminal : "";

  const update = (patch: OverviewSearch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className={ui.h1}>{t("consoleOverviewTitle")}</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {formatIsoDate(date, lang)}
            {date === today && ` · ${t("consoleToday")}`}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("serviceDate")}</span>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => update({ date: e.target.value && e.target.value !== today ? e.target.value : undefined })}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
            <BsDateHint date={date} />
          </label>
          {date !== today && (
            <button
              type="button"
              onClick={() => update({ date: undefined })}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-900"
            >
              {t("consoleToday")}
            </button>
          )}
        </div>
      </div>

      <OverviewHeadlines date={date} today={today} />

      <OverviewCorridors
        range={range}
        today={today}
        colorBy={search.color ?? "occupancy"}
        selected={search.corridor}
        onRange={(r) => update({ from: r.from, to: r.to })}
        onColorBy={(color) => update({ color: color === "demand" ? "demand" : undefined })}
        onSelect={(corridor) => update({ corridor })}
      />

      <OverviewDepartures date={date} today={today} terminal={terminal} onTerminal={(code) => update({ terminal: code || undefined })} />
    </div>
  );
}
